import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Speaker {
  id: string;
  name: {
    firstName: string;
    lastName: string;
    fullName: string;
  };
  email: string;
}

export interface ExternalSpeaker {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
}

export interface VirtualVenue {
  url: string;
  source: string;
}

export interface LearningContent {
  id: string;
  title: string;
  contentType: string;
  duration: number;
  difficultyLevel: string;
}

export interface AgendaDetails {
  id: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  startDate: string;
  endDate: string;
  zoneId: string;
  status: string;
  skills: string[];
  speakers: string[];
  searchTags?: {
    defaultTag: string[];
  };
  coSpeakers: string[];
  externalSpeakers: ExternalSpeaker[];
  venue: string;
  virtualVenue: VirtualVenue | null;
  eventMode: string;
  learningContent: LearningContent[];
  sessionId: string;
  config?: {
    duration?: number;
    reminderConfig?: {
      duration: number;
      trigger: string;
    };
  };
  branding?: {
    thumbnail: string[];
    promo: string[];
  };
}

export interface AgendaDetailsState {
  agendas: AgendaDetails[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

const initialState: AgendaDetailsState = {
  agendas: [],
  isLoading: false,
  error: null,
  lastUpdated: null
};

const agendaDetailsSlice = createSlice({
  name: "agendaDetails",
  initialState,
  reducers: {
    setAgendaDetails: (
      state,
      action: PayloadAction<Partial<AgendaDetailsState>>
    ) => {
      return {
        ...state,
        ...action.payload
      };
    },
    addAgendaDetails: (state, action: PayloadAction<AgendaDetails>) => {
      const newAgenda = action.payload;
      const existingIndex = state.agendas.findIndex(
        (a) => a.id === newAgenda.id
      );

      if (existingIndex !== -1) {
        const newAgendas = state.agendas.map((agenda) =>
          agenda.id === newAgenda.id ? newAgenda : agenda
        );
        return {
          ...state,
          agendas: newAgendas
        };
      } else {
        return {
          ...state,
          agendas: [...state.agendas, newAgenda]
        };
      }
    },
    updateAgendaDetails: (state, action: PayloadAction<AgendaDetails>) => {
      const index = state.agendas.findIndex((s) => s.id === action.payload.id);
      if (index === -1) {
        return state;
      }

      const newAgendas = state.agendas.map((agenda, i) =>
        i === index ? { ...agenda, ...action.payload } : agenda
      );

      return {
        ...state,
        agendas: newAgendas
      };
    },
    deleteAgendaDetails: (state, action: PayloadAction<string>) => {
      const agendaId = action.payload;
      const existingAgendas = [...state.agendas];
      const updatedAgendas = existingAgendas.filter(
        (agenda) => agenda.id !== agendaId
      );
      return {
        ...state,
        agendas: updatedAgendas
      };
    },
    clearAgendaDetails: (state) => {
      return {
        ...initialState,
        isLoading: false,
        error: null
      };
    }
  }
});

export const {
  setAgendaDetails,
  clearAgendaDetails,
  addAgendaDetails,
  updateAgendaDetails,
  deleteAgendaDetails
} = agendaDetailsSlice.actions;

export default agendaDetailsSlice.reducer;
